import { ChevronRight } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useMe } from '@/api/domains/merchant-settings';
import { usePageTitle } from '@/components/layout/page-title';
import { PlusBadge } from '@/components/plus-badge';
import { cn } from '@/lib/utils';

import { FreeShippingCountdownIcon, OutOfHomePopupIcon, PromiseDateIcon } from './widget-icons';

type WidgetEntry = {
  key: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconClassName: string;
  to?: string;
  requiresPlus?: boolean;
};

// Order, icons and sizes mirror the Figma Widgets view (node 712:15225).
const WIDGETS: WidgetEntry[] = [
  {
    key: 'promiseDate',
    Icon: PromiseDateIcon,
    iconClassName: 'size-[30px]',
    to: '/widgets/promise-date',
  },
  {
    key: 'freeShippingCountdown',
    Icon: FreeShippingCountdownIcon,
    iconClassName: 'size-8',
    to: '/widgets/free-shipping',
  },
  {
    key: 'oohPopup',
    Icon: OutOfHomePopupIcon,
    iconClassName: 'size-8 rotate-180',
    to: '/widgets/ooh-popup',
    requiresPlus: true,
  },
];

export function WidgetsPage() {
  const { t } = useTranslation('widgets');
  usePageTitle(t('title'));
  const isShopifyPlus = useMe().data?.merchant?.is_shopify_plus ?? false;

  return (
    <div className="mx-auto flex w-full max-w-[944px] flex-col gap-10">
      <h2 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('title')}</h2>

      <div className="flex flex-col gap-6">
        {WIDGETS.map(({ key, Icon, iconClassName, to, requiresPlus }) => {
          const locked = Boolean(requiresPlus) && !isShopifyPlus;
          const cardClassName = cn(
            'block rounded-[4px] border border-[#e5e5e5] bg-card',
            to && !locked && 'transition-colors hover:border-black',
            locked && 'cursor-not-allowed opacity-60',
          );

          const content = (
            <div className="flex items-center justify-between gap-3 p-6">
              <div className="flex flex-1 items-start gap-[15px]">
                <div className="flex size-[68px] shrink-0 items-center justify-center rounded-[12px] bg-[#f7f7f7] text-black">
                  <Icon className={iconClassName} />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="flex items-center gap-2.5">
                    <h3 className="text-xl font-medium tracking-[-0.6px] text-black">
                      {t(`cards.${key}.title`)}
                    </h3>
                    {requiresPlus && !isShopifyPlus ? <PlusBadge /> : null}
                  </div>
                  <p className="max-w-[688px] text-base text-muted-foreground">
                    {t(`cards.${key}.description`)}
                  </p>
                </div>
              </div>
              <ChevronRight className="size-6 shrink-0 text-black" strokeWidth={1} />
            </div>
          );

          return to && !locked ? (
            <Link key={key} to={to} className={cardClassName}>
              {content}
            </Link>
          ) : (
            <div key={key} className={cardClassName} aria-disabled={locked || undefined}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
